import type { PlanFeatureInput } from '@frameio/graphql-client/generated';
import { useFolderPermissionSet } from '@frameio/permissions';
import { useFeatureGatePlanSuggestion } from '@frameio/plan-management/feature-gate/hooks';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import { MenuButton, MenuGroup } from '@frameio/vapor';
import { IconFolderAdd, IconFolderLocked } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/CreateFolderMenu';

// Feature flag constant for restricted folders functionality
const RESTRICTED_FOLDERS: PlanFeatureInput = 'RESTRICTED_FOLDERS';

export type CreateFolderMenuProps = {
  folderId: string;
  onNewFolderSelect: (folderId: string, restricted: boolean) => void;
  trackingSource: TrackingSourceType;
};

export function CreateFolderMenu({
  folderId,
  onNewFolderSelect,
  trackingSource,
}: CreateFolderMenuProps) {
  // Check if restricted folders feature is enabled for the current plan
  const restrictedFolderFeature =
    useFeatureGatePlanSuggestion(RESTRICTED_FOLDERS);

  const isRestrictedFoldersEnabled =
    restrictedFolderFeature?.includedInCurrentPlan;

  const permissions = useFolderPermissionSet({
    checks: ['canCreateChildren', 'canEditUserPermissions'],
    folderId,
  });

  const track = useTrackUnsafe();

  // Determine if user can create restricted folders (requires feature flag and permissions)
  const canCreateRestrictedFolder =
    isRestrictedFoldersEnabled && permissions.canEditUserPermissions;

  const handleCreateFolderPress =
    (restricted = false) =>
    () => {
      track('folder-created', {
        source: trackingSource,
        is_restricted: restricted,
      });

      onNewFolderSelect(folderId, restricted);
    };

  if (!permissions.canCreateChildren) return null;

  if (!canCreateRestrictedFolder) {
    return (
      <MenuButton
        iconBefore={<IconFolderAdd />}
        onPress={handleCreateFolderPress()}
      >
        {TEXT.newFolder}
      </MenuButton>
    );
  }

  return (
    <MenuButton
      iconBefore={<IconFolderAdd />}
      submenu={
        <MenuGroup>
          <MenuButton
            iconBefore={<IconFolderAdd />}
            onPress={handleCreateFolderPress()}
          >
            {TEXT.newFolder}
          </MenuButton>

          <MenuButton
            iconBefore={<IconFolderLocked />}
            onPress={handleCreateFolderPress(true)}
          >
            {TEXT.newRestrictedFolder}
          </MenuButton>
        </MenuGroup>
      }
    >
      {TEXT.newFolder}
    </MenuButton>
  );
}

TEXT.DEFINE({
  /** Menu item label for creating a new sub-folder */
  newFolder: `New Folder`,
  /** Menu item label for creating a new restricted sub-folder */
  newRestrictedFolder: `New Restricted Folder`,
});
